import { FC, useEffect, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import apiService from '@/services/api.service';
import { authService } from '@/services/auth.service';
import { useAuthContext } from '@/contexts/AuthContext';
import { LoadingSpinner } from '@/components';

interface PublicSector {
  id: string;
  name: string;
}

type FieldErrors = Record<string, string[] | string>;

const CadastroMatriculaPage: FC = () => {
  const { isAuthenticated, isLoading: authLoading } = useAuthContext();
  const navigate = useNavigate();

  const [sectors, setSectors] = useState<PublicSector[]>([]);
  const [name, setName] = useState('');
  const [matricula, setMatricula] = useState('');
  const [sectorId, setSectorId] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');

  const [loadingSectors, setLoadingSectors] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [generalError, setGeneralError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    apiService
      .get<PublicSector[]>('/auth/registration/sectors/')
      .then((res) => setSectors(res.data))
      .catch(() => setGeneralError('Não foi possível carregar os setores. Tente novamente mais tarde.'))
      .finally(() => setLoadingSectors(false));
  }, []);

  if (authLoading) {
    return (
      <div className="tema-escuro-fixo flex h-screen items-center justify-center bg-[#001420]">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  const showErrors = (data: unknown): string | null => {
    const root = (data as { error?: { errors?: FieldErrors; message?: string } })?.error;
    if (root?.errors) {
      setErrors(root.errors);
      return null;
    }
    return root?.message || 'Não foi possível enviar o cadastro. Verifique os dados e tente novamente.';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    setGeneralError('');

    if (password !== passwordConfirm) {
      setErrors({ password_confirm: 'As senhas não conferem' });
      return;
    }

    setIsSubmitting(true);
    try {
      await authService.registerMatricula({
        name: name.trim(),
        matricula: matricula.trim(),
        sector_id: sectorId,
        password,
        password_confirm: passwordConfirm,
      });
      setSuccess(true);
    } catch (err: unknown) {
      const axiosErr = err as { response?: { status?: number; data?: unknown } };
      if (axiosErr?.response?.status === 429) {
        setGeneralError('Muitas tentativas em pouco tempo. Aguarde alguns minutos e tente novamente.');
      } else {
        const msg = showErrors(axiosErr?.response?.data);
        if (msg) setGeneralError(msg);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const fieldError = (key: string): string | undefined => {
    const raw = errors[key];
    if (!raw) return undefined;
    return Array.isArray(raw) ? raw[0] : raw;
  };

  if (success) {
    return (
      <div className="tema-escuro-fixo flex min-h-screen items-center justify-center bg-[#001420] px-4 py-12">
        <div className="w-full max-w-md space-y-6 rounded-2xl border border-white/10 bg-[#002030] p-8 text-center">
          <img src="/hcompany-logo.png" alt="h.company" className="mx-auto w-[220px] mb-4" />
          <div className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-[#FCB428]/10">
            <span className="material-symbols-outlined text-4xl text-[#FCB428]">hourglass_top</span>
          </div>
          <h1 className="text-2xl font-bold text-white">Cadastro enviado</h1>
          <p className="text-sm text-white/70 leading-relaxed">
            Seu cadastro foi registrado e está aguardando aprovação do administrador. Você poderá fazer login após a liberação.
          </p>
          <div className="rounded-lg border border-[#FCB428]/20 bg-[#FCB428]/5 p-4 text-left text-xs text-white/60">
            <p><strong className="text-[#FCB428]">Matrícula:</strong> {matricula}</p>
            <p className="mt-1">Anote sua matrícula — você vai usá-la pra entrar na plataforma.</p>
          </div>
          <Link
            to="/login"
            className="inline-flex items-center justify-center rounded-lg border border-[#FCB428]/30 px-4 py-2 text-sm font-medium text-[#FCB428] hover:bg-[#FCB428]/10"
          >
            Voltar ao login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="tema-escuro-fixo flex min-h-screen items-center justify-center bg-[#001420] px-4 py-12 sm:px-6 lg:px-8">
      <div className="w-full max-w-md space-y-6">
        <div className="flex flex-col items-center">
          <img src="/hcompany-logo.png" alt="h.company" className="w-[220px] mb-5" />
          <h1 className="text-center text-3xl font-bold">
            <span className="text-[#7ED6DD]">h</span>
            <span className="text-[#FCB428]">.lab</span>
          </h1>
          <p className="mt-2 text-center text-sm text-[#7ED6DD]">
            Cadastro por matrícula
          </p>
          <p className="mt-1 text-center text-xs text-white/50 max-w-sm">
            Se você não tem email corporativo, preencha os dados abaixo. Seu acesso será liberado após aprovação do administrador.
          </p>
        </div>

        {generalError && (
          <div
            data-testid="general-error"
            className="rounded-lg border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-400"
          >
            {generalError}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="name" className="block text-xs font-medium text-white/60 mb-1">Nome completo</label>
            <input
              id="name"
              name="name"
              type="text"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="block w-full rounded-lg border border-[rgba(252,180,40,0.15)] bg-[#003046] px-4 py-2.5 text-sm text-white placeholder-white/40 focus:border-[#FCB428] focus:outline-none focus:ring-1 focus:ring-[#FCB428]"
              placeholder="Ex: João da Silva"
            />
            {fieldError('name') && <p className="mt-1 text-xs text-rose-400">{fieldError('name')}</p>}
          </div>

          <div>
            <label htmlFor="matricula" className="block text-xs font-medium text-white/60 mb-1">Matrícula</label>
            <input
              id="matricula"
              name="matricula"
              type="text"
              required
              value={matricula}
              onChange={(e) => setMatricula(e.target.value)}
              className="block w-full rounded-lg border border-[rgba(252,180,40,0.15)] bg-[#003046] px-4 py-2.5 text-sm text-white placeholder-white/40 focus:border-[#FCB428] focus:outline-none focus:ring-1 focus:ring-[#FCB428]"
              placeholder="Ex: PROD0042"
              autoCapitalize="characters"
            />
            {fieldError('matricula') && <p className="mt-1 text-xs text-rose-400">{fieldError('matricula')}</p>}
          </div>

          <div>
            <label htmlFor="sector" className="block text-xs font-medium text-white/60 mb-1">Área/Setor</label>
            <select
              id="sector"
              name="sector"
              required
              value={sectorId}
              onChange={(e) => setSectorId(e.target.value)}
              disabled={loadingSectors}
              className="block w-full rounded-lg border border-[rgba(252,180,40,0.15)] bg-[#003046] px-4 py-2.5 text-sm text-white placeholder-white/40 focus:border-[#FCB428] focus:outline-none focus:ring-1 focus:ring-[#FCB428] disabled:opacity-50"
            >
              <option value="">{loadingSectors ? 'Carregando…' : 'Selecione um setor'}</option>
              {sectors.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
            {fieldError('sector_id') && <p className="mt-1 text-xs text-rose-400">{fieldError('sector_id')}</p>}
          </div>

          <div>
            <label htmlFor="password" className="block text-xs font-medium text-white/60 mb-1">Senha</label>
            <input
              id="password"
              name="password"
              type="password"
              required
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="block w-full rounded-lg border border-[rgba(252,180,40,0.15)] bg-[#003046] px-4 py-2.5 text-sm text-white placeholder-white/40 focus:border-[#FCB428] focus:outline-none focus:ring-1 focus:ring-[#FCB428]"
              placeholder="Mínimo 8 caracteres"
              autoComplete="new-password"
            />
            {fieldError('password') && <p className="mt-1 text-xs text-rose-400">{fieldError('password')}</p>}
          </div>

          <div>
            <label htmlFor="password_confirm" className="block text-xs font-medium text-white/60 mb-1">Confirmar senha</label>
            <input
              id="password_confirm"
              name="password_confirm"
              type="password"
              required
              minLength={8}
              value={passwordConfirm}
              onChange={(e) => setPasswordConfirm(e.target.value)}
              className="block w-full rounded-lg border border-[rgba(252,180,40,0.15)] bg-[#003046] px-4 py-2.5 text-sm text-white placeholder-white/40 focus:border-[#FCB428] focus:outline-none focus:ring-1 focus:ring-[#FCB428]"
              placeholder="Digite a senha novamente"
              autoComplete="new-password"
            />
            {fieldError('password_confirm') && (
              <p className="mt-1 text-xs text-rose-400">{fieldError('password_confirm')}</p>
            )}
          </div>

          <button
            type="submit"
            disabled={isSubmitting || loadingSectors}
            data-testid="submit-register-matricula"
            className="relative flex w-full justify-center rounded-lg bg-[#FCB428] px-3 py-3 text-sm font-semibold text-[#001420] hover:bg-[#FFC94D] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#FCB428] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? 'Enviando…' : 'Enviar cadastro'}
          </button>
        </form>

        <div className="pt-2 text-center">
          <button
            type="button"
            onClick={() => navigate('/login')}
            className="text-xs text-white/50 hover:text-white/80"
          >
            ← Voltar para o login
          </button>
        </div>
      </div>
    </div>
  );
};

export default CadastroMatriculaPage;
